import React, { useState, useEffect } from 'react';
import RuleForm from './RuleForm';

/**
 * AlertRules - Manage application alert rules
 *
 * Props:
 *   - configs: MonitoringControl_configs object
 *   - devices: Array of monitored devices
 *   - onSave: function to save updated configs
 *   - saveStatus: 'success' | 'error' | ''
 */
const AlertRules = ({ configs, devices, onSave, saveStatus }) => {
  const [localConfigs, setLocalConfigs] = useState(configs);
  const [editingRule, setEditingRule] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Sync local state when props change
  useEffect(() => {
    setLocalConfigs(configs);
  }, [configs]);

  // Get application alert rules
  const rules = localConfigs?.application_alerts?.rules || [];

  // Filter devices to only include sensors (mode === 'sensor' or has monitored_parameters)
  const sensorDevices = (devices || []).filter(device =>
    device.mode === 'sensor' ||
    device.monitored_parameters?.length > 0 ||
    device.platform === 'airthings' ||
    device.platform === 'netatmo'
  );

  // Helper to get device name from device_id
  const getDeviceName = (deviceId) => {
    const device = devices.find(d =>
      (d.device_id === deviceId) || (d.id === deviceId)
    );
    return device?.name || deviceId;
  };

  // Get available parameters for a device
  const getDeviceParameters = (deviceId) => {
    const device = devices.find(d =>
      (d.device_id === deviceId) || (d.id === deviceId)
    );
    return device?.monitored_parameters || ['temp', 'hum', 'co2', 'battery'];
  };

  // Format condition for display
  const formatCondition = (condition) => {
    if (!condition) return 'Unknown';

    const operatorMap = {
      'lt': '<',
      'lte': '<=',
      'gt': '>',
      'gte': '>='
    };

    const operator = operatorMap[condition.operator] || condition.operator;

    if (condition.type === 'snapshot') {
      let desc = `Current value ${operator} ${condition.threshold}`;
      if (condition.duration_minutes) {
        desc += ` for ${condition.duration_minutes} min`;
      }
      return desc;
    } else if (condition.type === 'average') {
      return `${condition.period_hours}h avg ${operator} ${condition.threshold}`;
    }

    return `${operator} ${condition.threshold}`;
  };

  // Format parameter name
  const formatParameter = (param) => {
    const paramMap = {
      'temp': 'Temperature',
      'hum': 'Humidity',
      'co2': 'CO2',
      'battery': 'Battery'
    };
    return paramMap[param] || param;
  };

  // Handle rule enable/disable toggle
  const handleToggleRule = (ruleId) => {
    const updatedRules = rules.map(r =>
      r.id === ruleId ? { ...r, enabled: !r.enabled } : r
    );
    const updatedConfigs = {
      ...localConfigs,
      application_alerts: {
        ...localConfigs.application_alerts,
        rules: updatedRules
      }
    };

    setLocalConfigs(updatedConfigs);
    onSave(updatedConfigs);
  };

  // Handle rule deletion
  const handleDeleteRule = (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) {
      return;
    }

    const updatedRules = rules.filter(r => r.id !== ruleId);
    const updatedConfigs = {
      ...localConfigs,
      application_alerts: {
        ...localConfigs.application_alerts,
        rules: updatedRules
      }
    };

    setLocalConfigs(updatedConfigs);
    onSave(updatedConfigs);
  };

  // Handle save rule (add or update)
  const handleSaveRule = (rule) => {
    let updatedRules;

    if (editingRule) {
      // Update existing rule
      updatedRules = rules.map(r => r.id === editingRule.id ? rule : r);
    } else {
      // Add new rule
      updatedRules = [...rules, rule];
    }

    const updatedConfigs = {
      ...localConfigs,
      application_alerts: {
        ...localConfigs.application_alerts,
        rules: updatedRules
      }
    };

    setLocalConfigs(updatedConfigs);
    onSave(updatedConfigs);
    setEditingRule(null);
    setShowAddForm(false);
  };

  return (
    <div className="alert-rules">
      <div className="rules-header">
        <h3>Application Alert Rules</h3>
        <button
          className="add-rule-button"
          onClick={() => {
            setEditingRule(null);
            setShowAddForm(true);
          }}
        >
          + Add Rule
        </button>
      </div>

      {saveStatus === 'success' && <p className="save-status success">Rules saved successfully</p>}
      {saveStatus === 'error' && <p className="save-status error">Error saving rules</p>}

      {rules.length === 0 && !showAddForm ? (
        <div className="rules-empty">
          <p>No alert rules configured.</p>
          <p>Create rules to get notified when device parameters exceed thresholds.</p>
        </div>
      ) : (
        <div className="rules-list">
          {rules.map((rule) => (
            <div key={rule.id} className={`rule-card ${rule.enabled === false ? 'rule-disabled' : ''}`}>
              <div className="rule-card-header">
                <div className="rule-header-left">
                  <label className="rule-toggle">
                    <input
                      type="checkbox"
                      checked={rule.enabled !== false}
                      onChange={() => handleToggleRule(rule.id)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <div className="rule-title">
                    <span className="rule-name">{rule.name || rule.id}</span>
                    <span className="rule-id-small">{rule.id}</span>
                  </div>
                </div>
                <div className="rule-actions">
                  <button
                    className="edit-button"
                    onClick={() => {
                      setEditingRule(rule);
                      setShowAddForm(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="delete-button"
                    onClick={() => handleDeleteRule(rule.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="rule-card-body">
                <div className="rule-detail">
                  <span className="detail-label">Device:</span>
                  <span className="detail-value">{getDeviceName(rule.device_id)}</span>
                </div>
                <div className="rule-detail">
                  <span className="detail-label">Parameter:</span>
                  <span className="detail-value">{formatParameter(rule.parameter)}</span>
                </div>
                <div className="rule-detail">
                  <span className="detail-label">Condition:</span>
                  <span className="detail-value">{formatCondition(rule.condition)}</span>
                </div>
                <div className="rule-detail">
                  <span className="detail-label">Severity:</span>
                  <span className={`detail-value severity-${rule.severity || 'warning'}`}>
                    {rule.severity || 'warning'}
                  </span>
                </div>
                <div className="rule-detail">
                  <span className="detail-label">Cooldown:</span>
                  <span className="detail-value">{(rule.cooldown_minutes || 480) / 60}h</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddForm && (
        <RuleForm
          rule={editingRule}
          devices={sensorDevices}
          onSave={handleSaveRule}
          onCancel={() => {
            setEditingRule(null);
            setShowAddForm(false);
          }}
          existingRuleIds={rules.map(r => r.id)}
        />
      )}
    </div>
  );
};

export default AlertRules;
